// src/components/layout/PublicHeader.jsx - Enhanced with Swipe Gesture Navigation
import React, { useState, useEffect } from 'react';
import { X, Home, Users, CreditCard, HelpCircle, LogIn, UserPlus, Newspaper } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../ui/LanguageSwitcher';

const PublicHeader = ({ user, avatarUrl, onLogout, authStateResolved }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  // Swipe Drawer State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerMounted, setIsDrawerMounted] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
    const [isDashboardHovered, setIsDashboardHovered] = useState(false);
  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Handle scroll effect for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDrawerOpen]);

  // Keep drawer mounted briefly for close animation
  useEffect(() => {
    if (isDrawerOpen) {
      setIsDrawerMounted(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsDrawerMounted(false);
    }, 320);

    return () => clearTimeout(timeoutId);
  }, [isDrawerOpen]);

  // Close drawer when route changes
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
    setIsDrawerOpen(false);
  };

  const handleSignUp = () => {
    handleNavigate('/auth?mode=signup');
  };

  // Swipe Detection Functions
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Open drawer on left swipe from right edge
    if (isLeftSwipe && touchStart > window.innerWidth - 50) {
      setIsDrawerOpen(true);
    }

    // Close drawer on right swipe when drawer is open
    if (isRightSwipe && isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  };

  // Public navigation links (visible to all users)
  const publicNavLinks = [
    {
      path: '/',
      name: t('nav.home'),
      icon: <Home className="w-5 h-5" />
    },
    {
      path: '/blog',
      name: t('nav.blog', 'Blog'),
      icon: <Newspaper className="w-5 h-5" />
    },
    {
      path: '/faq',
      name: t('nav.faq'),
      icon: <HelpCircle className="w-5 h-5" />
    },
    {
      path: '/about',
      name: t('nav.about'),
      icon: <Users className="w-5 h-5" />
    },
    {
      path: '/pricing',
      name: t('nav.pricing', 'Pricing'),
      icon: <CreditCard className="w-5 h-5" />
    }
  ];

  return (
    <>
      <header
        className={`
          bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40 safe-area-inset-top
          transition-all duration-200
          ${isScrolled ? 'shadow-lg' : ''}
        `}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center transition-all duration-300 ${isScrolled ? 'h-14' : 'h-16'}`}>

            {/* Left: Logo and Desktop Navigation */}
            <div className="flex items-center gap-4 lg:gap-16 min-w-0">
              {/* Logo */}
              <button
                onClick={() => handleNavigate('/')}
                className="flex items-center flex-shrink-0 transition-transform hover:scale-105 active:scale-95 touch-manipulation min-h-[44px] -ml-1 w-[110px] sm:w-[140px]"
              >
                <img
                  src="/logo.png?v=3"
                  alt="Logami Logo"
                  className="h-10 w-10 sm:h-12 sm:w-12 object-contain scale-[2.6] sm:scale-[3] origin-left"
                />
              </button>

             {/* Desktop Navigation */}
<nav className="hidden lg:flex items-center space-x-6">
  {publicNavLinks.map(link => {
    return (
      <button
        key={link.path}
        onClick={() => handleNavigate(link.path)}
        style={{
          position: 'relative',
          padding: '0.5rem 0.25rem',
          cursor: 'pointer'
        }}
        className="touch-manipulation min-h-[44px]"
        onMouseEnter={(e) => {
          e.currentTarget.querySelector('.nav-text').style.color = '#3A86FF';
          e.currentTarget.querySelector('.nav-underline').style.width = '100%';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.querySelector('.nav-text').style.color = '#000';
          e.currentTarget.querySelector('.nav-underline').style.width = '0';
        }}
      >
        <span
          className="nav-text font-medium"
          style={{
            color: '#000', // Always black by default
            transition: 'color 0.3s'
          }}
        >
          {link.name}
        </span>

        {/* Animated underline */}
        <span
          className="nav-underline"
          style={{
            position: 'absolute',
            left: '0',
            bottom: '0',
            width: '0', // Always hidden by default
            height: '2px',
            backgroundColor: '#3A86FF',
            transition: 'width 0.3s ease'
          }}
        />
      </button>
    );
  })}
</nav>
            </div>

            {/* Right: Mobile Navigation + Language Switcher + Auth Buttons */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Language Switcher */}
              <div className="hidden lg:block">
                <LanguageSwitcher />
              </div>

              {/* Auth Buttons with State Resolution Check */}
              {authStateResolved ? (
                user ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleNavigate('/dashboard')}
                      onMouseEnter={() => setIsDashboardHovered(true)}
                      onMouseLeave={() => setIsDashboardHovered(false)}
                      className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-black touch-manipulation min-h-[44px] relative overflow-hidden cursor-pointer"
                    >
                      {/* Wave fill effect */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          width: isDashboardHovered ? '100%' : '0%',
                          background: 'linear-gradient(to right, #60a5fa, #3b82f6, #1d4ed8)', // Blue gradient
                          transition: 'width 0.7s ease-out',
                          zIndex: 0
                        }}
                      />

                      {/* Button Icon */}
                      <Home
                        className="w-4 h-4 relative z-10"
                        style={{
                          color: isDashboardHovered ? '#ffffff' : '#000000', // Changed default to black
                          transition: 'color 0.5s'
                        }}
                      />

                      {/* Button Text */}
                      <span
                        className="font-medium relative z-10"
                        style={{
                          color: isDashboardHovered ? '#ffffff' : '#000000', // Changed default to black
                          transition: 'color 0.5s'
                        }}
                      >
                        {t('nav.dashboard')}
                      </span>
                    </button>
                    <button
  onClick={() => handleNavigate('/auth')}
  className="sm:hidden bg-black text-white px-3 py-2 rounded-lg font-semibold hover:bg-[#3A86FF] transition-all duration-300 touch-manipulation min-h-[44px] text-sm border-2 border-black hover:border-[#3A86FF]"
>
                      {t('nav.login')}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Mobile primary CTA only */}
                    <button
                      onClick={handleSignUp}
                      className="lg:hidden bg-black text-white px-3 py-2 rounded-lg font-semibold hover:bg-[#3A86FF] transition-all duration-300 touch-manipulation min-h-[44px] text-sm border-2 border-black hover:border-[#3A86FF]"
                      aria-label={t('nav.getStarted') || 'Sign Up'}
                    >
                      {t('nav.getStarted') || 'Sign Up'}
                    </button>

                    {/* Desktop Login/Sign Up Buttons */}
                    <div className="hidden lg:flex items-center gap-2 lg:gap-3">
                      {/* Login Button */}
                      <button
                        onClick={() => handleNavigate('/auth')}
                        style={{ position: 'relative', cursor: 'pointer' }}
                        className="px-4 py-2 lg:px-5 lg:py-2.5 text-sm lg:text-base font-semibold touch-manipulation min-h-[44px]"
                        onMouseEnter={(e) => {
                          e.currentTarget.querySelector('.login-text').style.color = '#3A86FF';
                          e.currentTarget.querySelector('.login-underline').style.width = '80%';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.querySelector('.login-text').style.color = '#000';
                          e.currentTarget.querySelector('.login-underline').style.width = '0';
                        }}
                      >
                        <span className="login-text" style={{ color: '#000', transition: 'color 0.3s' }}>
                          Login
                        </span>
                        <span
                          className="login-underline"
                          style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bottom: '6px',
                            width: '0',
                            height: '2px',
                            backgroundColor: '#3A86FF',
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </button>

                      {/* Sign Up Button */}
                      <button
                        onClick={handleSignUp}
                        style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden' }}
                        className="px-6 py-2 lg:px-8 lg:py-2.5 text-sm lg:text-base font-bold bg-black text-white rounded-lg border-2 border-black transition-all duration-300 touch-manipulation min-h-[44px]"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#3A86FF';
                          e.currentTarget.style.borderColor = '#3A86FF';
                          e.currentTarget.querySelector('.shine-effect').style.transform = 'translateX(100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#000';
                          e.currentTarget.style.borderColor = '#000';
                          e.currentTarget.querySelector('.shine-effect').style.transform = 'translateX(-100%)';
                        }}
                      >
                        <span style={{ position: 'relative', zIndex: 10 }}>
                          {t('nav.getStarted') || 'Sign Up'}
                        </span>
                        {/* Shine effect */}
                        <span
                          className="shine-effect"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                            transform: 'translateX(-100%)',
                            transition: 'transform 0.5s ease',
                            pointerEvents: 'none'
                          }}
                        />
                      </button>
                    </div>
                  </>
                )
              ) : (
                // Loading skeleton while auth state is resolving
                <div className="flex items-center gap-2">
                  <div className="hidden sm:block h-10 w-16 bg-gray-200 animate-pulse rounded-lg"></div>
                  <div className="h-10 w-20 sm:w-24 bg-gray-200 animate-pulse rounded-lg"></div>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsDrawerOpen((prev) => !prev)}
                className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation min-h-[44px] min-w-[44px]"
                aria-label={t('nav.menu')}
                aria-expanded={isDrawerOpen}
                aria-controls="public-mobile-drawer"
              >
                <span className="relative block w-5 h-5" aria-hidden="true">
                  <span
                    className={`absolute left-0 top-[3px] h-[2px] w-5 rounded-full bg-current transition-all duration-300 ${isDrawerOpen ? 'translate-y-[6px] rotate-45' : ''}`}
                  />
                  <span
                    className={`absolute left-0 top-[9px] h-[2px] w-5 rounded-full bg-current transition-all duration-200 ${isDrawerOpen ? 'opacity-0' : 'opacity-100'}`}
                  />
                  <span
                    className={`absolute left-0 top-[15px] h-[2px] w-5 rounded-full bg-current transition-all duration-300 ${isDrawerOpen ? '-translate-y-[6px] -rotate-45' : ''}`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Swipe Drawer Overlay - Mobile Only */}
      {isDrawerMounted && (
        <div className="fixed inset-0 z-50 lg:hidden pointer-events-none">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 backdrop-blur-sm transition-all duration-300 ${isDrawerOpen ? 'bg-black/50 opacity-100 pointer-events-auto' : 'bg-black/0 opacity-0'}`}
            onClick={() => setIsDrawerOpen(false)}
          />

          {/* Drawer - Slides from Right */}
          <div
            id="public-mobile-drawer"
            className={`
              absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl
              transform-gpu transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] pointer-events-auto
              ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}
            `}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">{t('nav.menu')}</h2>
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation min-h-[44px] min-w-[44px]"
                aria-label="Close menu"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="p-4">
              {!user && (
                <div className="mb-4 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleNavigate('/auth')}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border border-gray-200 text-gray-700 font-semibold min-h-[48px]"
                  >
                    <LogIn className="w-4 h-4" />
                    {t('nav.login')}
                  </button>
                  <button
                    onClick={handleSignUp}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-black text-white font-semibold min-h-[48px]"
                  >
                    <UserPlus className="w-4 h-4" />
                    {t('nav.getStarted') || 'Sign Up'}
                  </button>
                </div>
              )}
              <ul className="space-y-2">
                {publicNavLinks.map((link) => (
                  <li key={link.path}>
                    <button
                      onClick={() => handleNavigate(link.path)}
                      className={`
                        w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200
                        touch-manipulation min-h-[56px]
                        ${location.pathname === link.path
                          ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-sm font-semibold'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }
                      `}
                    >
                      <div className={`
                        w-10 h-10 rounded-lg flex items-center justify-center
                        ${location.pathname === link.path
                          ? 'bg-blue-200 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                        }
                      `}>
                        {link.icon}
                      </div>
                      <span className="font-medium text-base">{link.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Language Switcher in Drawer */}
            <div className="px-4 py-6 border-t border-gray-200 mt-4">
              <p className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wider">
                {t('settings.language')}
              </p>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}

      {/* Touch Detection Zone - Right Edge */}
      <div
        className="fixed right-0 top-0 h-full w-12 z-30 lg:hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ pointerEvents: isDrawerOpen ? 'none' : 'auto' }}
      />
    </>
  );
};

export default PublicHeader;
